import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import { Button } from "../../components/ui/CustomButton";
import InputFormField from "../../components/ui/inputs/InputFormField";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { sendPasswordResetEmailToUser } from "../../firebase/firebase";
import { useEffect } from "react";
import { showToast } from "../../utils/toast";

const editPwScheme = z.object({
  email: z
    .string()
    .trim()
    .min(1, "Please enter the email address you sign in with.")
    .email("That doesn't look like an email address. Please check it and try again."),
});

type EditPwScheme = z.infer<typeof editPwScheme>;

interface EditPwDialogProps {
  open: boolean;
  onClose: () => void;
  selectedValue: string;
  shouldDisable: boolean;
}

const EditPwDialog = (props: EditPwDialogProps) => {
  const { onClose, selectedValue, open, shouldDisable } = props;

  const formData = useForm<EditPwScheme>({
    mode: "onBlur",
    resolver: zodResolver(editPwScheme),
    defaultValues: {
      email: selectedValue,
    },
  });
  const { reset } = formData;

  // Start from the email the person has typed (or is signed in with) each time it opens.
  useEffect(() => {
    if (open) reset({ email: selectedValue ?? "" });
  }, [open, selectedValue, reset]);

  const sendResetLink = formData.handleSubmit(async ({ email }) => {
    const { error } = await sendPasswordResetEmailToUser(email);
    if (error) {
      formData.setError("email", { type: "manual", message: error });
      return;
    }
    showToast(`If ${email} has an account, we've sent it a link to reset your password. Please check your inbox, and your spam folder too.`, { type: "success" });
    onClose();
  });

  return (
    <Dialog onClose={onClose} open={open}>
      <div className="m-10">
        <DialogTitle id="simple-dialog-title" className="flex flex-row items-center justify-center">Reset Password</DialogTitle>
        <FormProvider {...formData}>
          <form noValidate onSubmit={sendResetLink}>
            <InputFormField
              name="email"
              label="Email Address"
              placeholder="Enter your email"
              autoComplete="email"
              className="mt-3 mb-3"
              disabled={shouldDisable}
            />
            <div className="flex flex-row justify-end">
              <Button
                type="submit"
                className="mt-3 mb-3"
                isLoading={formData.formState.isSubmitting}
                disabled={formData.formState.isSubmitting}
              >
                Send Reset Link to Email
              </Button>
            </div>
          </form>
        </FormProvider>
      </div>
    </Dialog>
  );
};

export default EditPwDialog;
